/**
 * landing-custom-canvas — the "Create your own" layout surface for the Event
 * Landing Page journey.
 *
 * WHY THIS IS A SEPARATE MODULE. `AllJourneys.tsx` is the four built-in
 * templates' screen and is already ~9.7k lines; a drag-and-resize grid with a
 * device model does not belong inlined in it. Everything here is pure —
 * geometry, palette metadata, contract (de)serialisation — plus three
 * presentational components. It performs no I/O: the screen owns the read and
 * the write, this module owns the layout.
 *
 * THE MODEL (decided with the operator, 2026-09-17, rounds 1–5)
 *  • 12-column grid at desktop, 8 at tablet, 4 at mobile.
 *  • ONE authored layout (desktop) + per-device OVERRIDES. An untouched block
 *    follows desktop, rescaled. Overrides are per AXIS, not per block, and a
 *    later desktop change WARNS before overwriting one (see `conflictsFor`).
 *  • Spans rescale ROUND-UP: a 5-of-12 becomes 4-of-8 and 2-of-4. Wider rather
 *    than narrower, so text stays readable.
 *  • Height is in ROW UNITS on the same grid. Content taller than its box
 *    SCROLLS inside it and the canvas marks the block — nothing is lost.
 *  • Rows may have EMPTY COLUMNS; you drop into the gap.
 *  • Dropping onto occupied cells PUSHES the blocks below down.
 *  • Title and CTA are ordinary grid blocks — resizable and movable, NOT
 *    deletable. The CTA additionally cannot be hidden on any device: a page
 *    with no way to register should not be publishable.
 *
 * PERSISTENCE. A block round-trips as the contract's `LandingBlock`
 * (`{uid, type, cfg}`, `cfg` a passthrough record), so the geometry travels
 * inside `cfg` under `grid` and the whole layout saves through the existing
 * `PATCH /v1/landing/{id}` with no contract change. Two consequences worth
 * knowing, both real:
 *   1. `EventLandingPresentationSchema` is `.strict()` and its
 *      `landingTemplate` is `z.enum(['banner','hero','split','long'])` — there
 *      is NO `'custom'` to save. So "this page uses a custom layout" is not a
 *      presentation field: it is derived from the blocks themselves
 *      (`carriesCustomLayout`), which is why `blankCustomLayout()` always
 *      seeds at least the two locked blocks.
 *   2. The PUBLIC page does not render `landingDesign.blocks` (see the note at
 *      `AllJourneys.tsx`'s `loadLandingDetail`). Saving is real; a visitor
 *      still gets the built-in template until a public renderer exists. The
 *      screen says so on the canvas rather than implying otherwise.
 */

import React from "react";

/* ── Devices ──────────────────────────────────────────────────────────────── */

export type DeviceId = "desktop" | "tablet" | "mobile";
export const DEVICES: DeviceId[] = ["desktop", "tablet", "mobile"];
export const DEVICE_COLS: Record<DeviceId, number> = { desktop: 12, tablet: 8, mobile: 4 };
export const DEVICE_LABEL: Record<DeviceId, string> = { desktop: "Desktop", tablet: "Tablet", mobile: "Mobile" };
/** Fallback canvas width per device, used only when the host does not pass one.
 *  The screen DOES pass one — from `resolveDeviceWidths()`, the same source the
 *  toolbar's device toggle uses — so the grid and the preview frame can never
 *  disagree about how wide "mobile" is. */
export const DEVICE_FRAME: Record<DeviceId, number> = { desktop: 1080, tablet: 768, mobile: 390 };

export const ROW_PX = 64;
export const GRID_GAP = 10;

/* ── Geometry ─────────────────────────────────────────────────────────────── */

export type AlignId = "left" | "center" | "right";

export interface GridBox {
  /** 0-based column start. */ c: number;
  /** Column span, ≥1. */ s: number;
  /** 0-based row start. */ r: number;
  /** Height in row units, ≥1. */ h: number;
  a: AlignId;
  v: boolean;
}

export type GridOverride = Partial<GridBox>;

export interface CustomBlock {
  uid: string;
  type: string;
  cfg: Record<string, unknown>;
  /** `"title"` and `"cta"` are the two undeletable blocks. */
  locked?: "title" | "cta" | null;
  desktop: GridBox;
  tablet: GridOverride;
  mobile: GridOverride;
}

/** The axes a tablet/mobile override may carry. Order is the inspector's. */
export const OVERRIDE_AXES: Array<{ key: keyof GridBox; label: string }> = [
  { key: "s", label: "Width" },
  { key: "r", label: "Order" },
  { key: "v", label: "Visibility" },
  { key: "h", label: "Height" },
  { key: "a", label: "Alignment" },
];

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Round-UP rescale of a span between column counts (round 5). */
export function rescaleSpan(span: number, from: number, to: number): number {
  return clamp(Math.ceil((span * to) / from), 1, to);
}

/** Column starts rescale round-DOWN so a block never leaves the grid on the
 *  right once its round-up span is applied. */
function rescaleCol(col: number, from: number, to: number): number {
  return clamp(Math.floor((col * to) / from), 0, to - 1);
}

/** The box a device actually renders: desktop base → rescaled → overrides. */
export function resolveBox(block: CustomBlock, device: DeviceId): GridBox {
  const base = block.desktop;
  const cols = DEVICE_COLS[device];
  let box: GridBox = { ...base };
  if (device !== "desktop") {
    const from = DEVICE_COLS.desktop;
    box = {
      ...base,
      s: rescaleSpan(base.s, from, cols),
      c: rescaleCol(base.c, from, cols),
    };
  }
  const over = device === "desktop" ? null : block[device];
  if (over) box = { ...box, ...over };
  box.s = clamp(box.s, 1, cols);
  box.c = clamp(box.c, 0, cols - box.s);
  box.h = Math.max(1, box.h);
  box.r = Math.max(0, box.r);
  if (block.locked === "cta") box.v = true; // hard guard (round 3)
  return box;
}

export function overriddenKeys(block: CustomBlock, device: DeviceId): Array<keyof GridBox> {
  if (device === "desktop") return [];
  const over = block[device] || {};
  return OVERRIDE_AXES.map((a) => a.key).filter((k) => Object.prototype.hasOwnProperty.call(over, k));
}

/** Which devices would have a desktop edit of `axis` masked by an override.
 *  The screen shows this BEFORE applying (round 5: "follow, but warn"). */
export function conflictsFor(blocks: CustomBlock[], uid: string, axis: keyof GridBox): DeviceId[] {
  const block = blocks.find((b) => b.uid === uid);
  if (!block) return [];
  return (["tablet", "mobile"] as DeviceId[]).filter((d) =>
    Object.prototype.hasOwnProperty.call(block[d] || {}, axis),
  );
}

function rowsSpan(box: GridBox): [number, number] {
  return [box.r, box.r + box.h];
}

function overlaps(a: GridBox, b: GridBox): boolean {
  const colHit = a.c < b.c + b.s && b.c < a.c + a.s;
  const [a0, a1] = rowsSpan(a);
  const [b0, b1] = rowsSpan(b);
  return colHit && a0 < b1 && b0 < a1;
}

/** Writes a SPARSE patch for `uid` on `device` — only the axes present in
 *  `patch` — then pushes every block it collides with DOWNWARD until the grid
 *  is clean (round 4: push, never swap or overlap).
 *
 *  SPARSENESS IS THE CONTRACT, not an optimisation. On tablet/mobile a written
 *  key becomes an OVERRIDE, so handing this a full resolved box would pin every
 *  axis the moment an author nudged one — exactly the "follow desktop unless I
 *  changed it" behaviour the device model rests on (round 5: overrides are per
 *  AXIS, not per block). Callers pass only what the gesture changed: a width
 *  drag sends `{s}`, a move sends `{c, r}`, a keyboard nudge sends one key.
 *
 *  The collision push is the one exception and legitimately writes `r` on the
 *  edited device: a block that has to move out of the way has genuinely moved
 *  there on that device. */
export function placeWithPush(
  blocks: CustomBlock[],
  device: DeviceId,
  uid: string,
  patch: Partial<GridBox>,
): CustomBlock[] {
  const write = (b: CustomBlock, p: Partial<GridBox>): CustomBlock =>
    device === "desktop"
      ? { ...b, desktop: { ...b.desktop, ...p } }
      : { ...b, [device]: { ...(b[device] || {}), ...p } } as CustomBlock;

  let next = Object.keys(patch).length
    ? blocks.map((b) => (b.uid === uid ? write(b, patch) : b))
    : blocks.slice();

  // Iterate: each pass moves the topmost colliding block below the mover.
  for (let guard = 0; guard < 200; guard += 1) {
    const boxes = next.map((b) => ({ uid: b.uid, box: resolveBox(b, device) }));
    let moved = false;
    for (let i = 0; i < boxes.length; i += 1) {
      for (let j = 0; j < boxes.length; j += 1) {
        if (i === j) continue;
        const a = boxes[i], b = boxes[j];
        if (!overlaps(a.box, b.box)) continue;
        // The block that starts lower (or, on a tie, is not the mover) yields.
        const yieldTo = a.uid === uid ? b : b.uid === uid ? a : (a.box.r <= b.box.r ? b : a);
        const anchor = yieldTo.uid === a.uid ? b : a;
        const target = anchor.box.r + anchor.box.h;
        if (yieldTo.box.r >= target) continue;
        next = next.map((blk) => (blk.uid === yieldTo.uid ? write(blk, { r: target }) : blk));
        moved = true;
        break;
      }
      if (moved) break;
    }
    if (!moved) break;
  }
  return next;
}

/** First row with nothing on it, for click-to-add (round 1: append). */
export function nextFreeRow(blocks: CustomBlock[], device: DeviceId): number {
  return blocks.reduce((max, b) => {
    const box = resolveBox(b, device);
    return Math.max(max, box.r + box.h);
  }, 0);
}

export function gridRows(blocks: CustomBlock[], device: DeviceId): number {
  return Math.max(4, nextFreeRow(blocks, device));
}

/* ── Palette ──────────────────────────────────────────────────────────────── */

export type PaletteGroup = "event" | "content";

export interface PaletteEntry {
  type: string;
  label: string;
  group: PaletteGroup;
  /** Default span at desktop (12-col) and default height in rows. */
  span: number;
  rows: number;
  /** What actually fills this block at runtime. `null` = authored copy only. */
  source: string | null;
  /** Set when a real capability is missing. Rendered verbatim on the tile and
   *  on the canvas — the honest-marking decision (round 1). */
  gap: string | null;
  cfg: Record<string, unknown>;
}

/**
 * The merged palette, grouped (round 2). `map` and `faq` existed in BOTH the
 * designer's block set and this page's own ten; the operator's call was ONE
 * entry each, under Event data, with the event-bound behaviour winning — so
 * there is no authored-address Map and no authored-copy FAQ tile here.
 *
 * `gap` text names the missing capability, not a feeling: image/gallery/video
 * have no upload op wired on this screen (the event hero still inlines data
 * URLs), and `social` has no tenant-level social URLs to read anywhere.
 */
export const PALETTE: PaletteEntry[] = [
  { type: "eventdetails", label: "Date & venue", group: "event", span: 5, rows: 2, source: "The event's start, end, timezone and location", gap: null, cfg: { showDate: true, showLocation: true, showAddToCalendar: true } },
  { type: "countdown", label: "Countdown", group: "event", span: 4, rows: 2, source: "Counts to the event's start", gap: null, cfg: { label: "Opens in", units: "Hours / min / sec" } },
  { type: "spaces", label: "Places left", group: "event", span: 4, rows: 2, source: "Capacity, registered and waitlist counts", gap: null, cfg: { showBar: true } },
  { type: "tickets", label: "Tickets", group: "event", span: 6, rows: 3, source: "The event's ticket types and prices", gap: null, cfg: {} },
  { type: "map", label: "Map", group: "event", span: 6, rows: 4, source: "The event's location, geocoded", gap: null, cfg: { zoom: "Building", directions: true } },
  { type: "related", label: "Related events", group: "event", span: 12, rows: 4, source: "Other published events in this tenant", gap: null, cfg: { rule: "category", count: 3 } },

  /* `speakers` is CONTENT, not event data: the event record carries no speaker
     roster — these are rows an author types (`lpSpeakerRows`). It sat under
     Event data whose hint promises "filled from the event record", which in a
     feature built on honest labelling was the wrong group. */
  { type: "speakers", label: "Speakers", group: "content", span: 12, rows: 4, source: null, gap: null, cfg: { rows: [] } },

  { type: "text", label: "Text", group: "content", span: 7, rows: 2, source: null, gap: null, cfg: { text: "", align: "Left", size: "Body" } },
  { type: "notice", label: "Notice", group: "content", span: 12, rows: 1, source: null, gap: null, cfg: { text: "", tone: "Neutral", icon: true } },
  { type: "button", label: "Button", group: "content", span: 3, rows: 1, source: null, gap: null, cfg: { text: "", style: "Primary", href: "" } },
  { type: "quote", label: "Quote", group: "content", span: 6, rows: 2, source: null, gap: null, cfg: { text: "", who: "" } },
  { type: "faq", label: "FAQ", group: "content", span: 12, rows: 3, source: null, gap: null, cfg: { rows: [] } },
  { type: "stats", label: "Stats", group: "content", span: 6, rows: 2, source: null, gap: null, cfg: { rows: [] } },
  { type: "included", label: "What's included", group: "content", span: 6, rows: 3, source: null, gap: null, cfg: { rows: [] } },
  { type: "sponsors", label: "Sponsors", group: "content", span: 12, rows: 3, source: null, gap: null, cfg: { rows: [], tiers: true } },
  { type: "share", label: "Share", group: "content", span: 4, rows: 1, source: null, gap: null, cfg: { channels: ["copy", "email", "linkedin"] } },
  { type: "image", label: "Image", group: "content", span: 6, rows: 3, source: null, gap: "No upload op is wired on this screen yet, so this takes a URL only.", cfg: { src: "", alt: "", caption: "" } },
  { type: "gallery", label: "Gallery", group: "content", span: 12, rows: 3, source: null, gap: "No upload op is wired on this screen yet, so this takes URLs only.", cfg: { urls: "", layout: "Grid" } },
  { type: "video", label: "Video", group: "content", span: 7, rows: 3, source: null, gap: "No upload op is wired on this screen yet, so this takes a URL only.", cfg: { url: "", caption: "" } },
  { type: "social", label: "Social", group: "content", span: 4, rows: 1, source: null, gap: "Nothing on this tenant carries social profile URLs, so these links have no source.", cfg: { channels: [] } },
  { type: "divider", label: "Divider", group: "content", span: 12, rows: 1, source: null, gap: null, cfg: { style: "Solid" } },
  { type: "spacer", label: "Spacer", group: "content", span: 12, rows: 1, source: null, gap: null, cfg: {} },
];

export const PALETTE_BY_TYPE: Record<string, PaletteEntry> = PALETTE.reduce(
  (acc, e) => { acc[e.type] = e; return acc; },
  {} as Record<string, PaletteEntry>,
);

/** The two locked blocks are not palette entries — they cannot be added or
 *  removed — but they still need labels and defaults. */
const LOCKED_META: Record<string, { label: string; span: number; rows: number; note: string }> = {
  "page.title": { label: "Title", span: 12, rows: 2, note: "The event's own name and summary." },
  "page.cta": { label: "Register", span: 12, rows: 1, note: "Cannot be hidden on any device." },
};

export function blockLabel(block: CustomBlock): string {
  if (LOCKED_META[block.type]) return LOCKED_META[block.type].label;
  const entry = PALETTE_BY_TYPE[block.type];
  return entry ? entry.label : block.type;
}

let uidSeq = 0;
function newUid(type: string): string {
  uidSeq += 1;
  return `${type.replace(/[^a-z]/g, "")}-${Date.now().toString(36)}-${uidSeq}`;
}

export function makeBlock(type: string, at: { c: number; r: number }, cols: number): CustomBlock {
  const entry = PALETTE_BY_TYPE[type];
  const span = entry ? rescaleSpan(entry.span, 12, cols) : cols;
  return {
    uid: newUid(type),
    type,
    cfg: entry ? { ...entry.cfg } : {},
    locked: null,
    desktop: {
      c: clamp(at.c, 0, Math.max(0, 12 - (entry ? entry.span : 12))),
      s: entry ? entry.span : 12,
      r: Math.max(0, at.r),
      h: entry ? entry.rows : 2,
      a: "left",
      v: true,
    },
    tablet: {},
    mobile: {},
  };
}

/** Step 1's blank page: title and CTA locked in (round 1), nothing else. */
export function blankCustomLayout(): CustomBlock[] {
  return [
    { uid: "page-title", type: "page.title", cfg: {}, locked: "title", desktop: { c: 0, s: 12, r: 0, h: 2, a: "left", v: true }, tablet: {}, mobile: {} },
    { uid: "page-cta", type: "page.cta", cfg: {}, locked: "cta", desktop: { c: 0, s: 12, r: 2, h: 1, a: "left", v: true }, tablet: {}, mobile: {} },
  ];
}

/* ── Contract (de)serialisation ───────────────────────────────────────────── */

export interface ContractBlock { uid: string; type: string; cfg: Record<string, unknown> }

export function toContractBlocks(blocks: CustomBlock[]): ContractBlock[] {
  return blocks.map((b) => ({
    uid: b.uid,
    type: b.type,
    cfg: {
      ...b.cfg,
      grid: { v: 1, desktop: b.desktop, tablet: b.tablet, mobile: b.mobile, locked: b.locked || null },
    },
  }));
}

function boxFrom(raw: unknown, fallback: GridBox): GridBox {
  const o = (raw || {}) as Record<string, unknown>;
  const num = (k: keyof GridBox, def: number): number => {
    const v = o[k as string];
    return typeof v === "number" && Number.isFinite(v) ? v : def;
  };
  const align = o.a === "center" || o.a === "right" ? (o.a as AlignId) : fallback.a;
  return {
    c: Math.max(0, num("c", fallback.c)),
    s: Math.max(1, num("s", fallback.s)),
    r: Math.max(0, num("r", fallback.r)),
    h: Math.max(1, num("h", fallback.h)),
    a: align,
    v: typeof o.v === "boolean" ? o.v : fallback.v,
  };
}

function overrideFrom(raw: unknown): GridOverride {
  const o = (raw || {}) as Record<string, unknown>;
  const out: GridOverride = {};
  (["c", "s", "r", "h"] as const).forEach((k) => {
    const v = o[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  });
  if (o.a === "left" || o.a === "center" || o.a === "right") out.a = o.a as AlignId;
  if (typeof o.v === "boolean") out.v = o.v;
  return out;
}

/** Tolerant read: a block whose `cfg.grid` is absent or malformed is still
 *  kept, stacked full width at the end, rather than dropped on the floor. */
export function fromContractBlocks(raw: unknown): CustomBlock[] {
  if (!Array.isArray(raw)) return [];
  let row = 0;
  const out: CustomBlock[] = [];
  raw.forEach((item) => {
    const b = (item || {}) as Record<string, unknown>;
    const uid = typeof b.uid === "string" && b.uid ? b.uid : newUid("block");
    const type = typeof b.type === "string" && b.type ? b.type : "text";
    const cfg = (b.cfg && typeof b.cfg === "object" ? { ...(b.cfg as Record<string, unknown>) } : {}) as Record<string, unknown>;
    const grid = cfg.grid as Record<string, unknown> | undefined;
    delete cfg.grid;
    const entry = PALETTE_BY_TYPE[type];
    const locked = grid && (grid.locked === "title" || grid.locked === "cta") ? (grid.locked as "title" | "cta") : null;
    const fallback: GridBox = {
      c: 0,
      s: entry ? entry.span : 12,
      r: row,
      h: entry ? entry.rows : 2,
      a: "left",
      v: true,
    };
    const desktop = boxFrom(grid ? grid.desktop : null, fallback);
    row = Math.max(row, desktop.r + desktop.h);
    out.push({
      uid, type, cfg, locked, desktop,
      tablet: overrideFrom(grid ? grid.tablet : null),
      mobile: overrideFrom(grid ? grid.mobile : null),
    });
  });
  return out;
}

/** Does this saved document use the custom canvas? There is no `'custom'` in
 *  the presentation enum (see the header), so the blocks are the signal. */
export function carriesCustomLayout(raw: unknown): boolean {
  if (!Array.isArray(raw) || raw.length === 0) return false;
  return raw.some((item) => {
    const cfg = (item || {}) as { cfg?: Record<string, unknown> };
    return Boolean(cfg.cfg && cfg.cfg.grid);
  });
}

/* ── Shared visual tokens ─────────────────────────────────────────────────── */

/* DESIGN-SYSTEM TOKENS ONLY, and no hex fallbacks (2026-09-17, review fix).
   The first cut of this module referenced six token names that do not exist —
   `--jrni-color-surface`, `--jrni-color-warning-*`, `--jrni-color-danger-text`,
   `--jrni-color-success-text` — each with a hardcoded hex fallback behind it.
   The colours rendered, so nothing looked broken, but every one of them was
   the fallback: the module was painting off-brand hexes (#1D8348 against the
   system's #027D47, #9C1B36 against #D01647) while appearing to be themed.
   A fallback is what hides that, so there are none here now: a wrong token
   name renders unstyled and gets noticed. Real names, from
   `_ds/colors_and_type.css`. */
const INK = "var(--jrni-color-text-strong)";
const SOFT = "var(--jrni-color-text-soft)";
const LINE = "var(--jrni-color-surface-border)";
const SURFACE = "var(--jrni-color-surface-card)";
const DANGER = "var(--jrni-color-status-danger-text)";
const SUCCESS = "var(--jrni-color-status-success-text)";
const WARN_TEXT = "var(--jrni-color-status-warning-text)";
const WARN_BG = "var(--jrni-color-status-warning-bg)";
/* No amber border token exists; mixing the warning ink into its own background
   keeps the edge inside the palette rather than inventing a seventh name. */
const WARN_EDGE = "color-mix(in oklab, var(--jrni-color-status-warning-text) 32%, var(--jrni-color-status-warning-bg))";

/* ── Palette list ─────────────────────────────────────────────────────────── */

export function CustomLayoutPalette(props: {
  brandColor: string;
  onAdd: (type: string) => void;
}): React.JSX.Element {
  const groups: Array<{ id: PaletteGroup; label: string; hint: string }> = [
    { id: "event", label: "Event data", hint: "Filled from the event record — not authored here." },
    { id: "content", label: "Content", hint: "Copy and media you write." },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: SOFT }}>
        Click a block to add it below everything on the page, or drag it onto the grid to place it exactly.
      </div>
      {groups.map((g) => (
        <div key={g.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: g.id === "event" ? props.brandColor : SOFT }}>{g.label}</span>
            <span style={{ fontSize: 12, color: SOFT }}>{g.hint}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
            {PALETTE.filter((e) => e.group === g.id).map((e) => (
              <button
                key={e.type}
                type="button"
                draggable
                onDragStart={(ev) => {
                  ev.dataTransfer.setData("application/x-jrni-block", e.type);
                  ev.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => props.onAdd(e.type)}
                title={e.source || e.gap || undefined}
                style={{
                  display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start",
                  padding: "9px 10px", borderRadius: 8, cursor: "grab", textAlign: "left",
                  background: g.id === "event" ? `color-mix(in oklab, ${props.brandColor} 6%, ${SURFACE})` : SURFACE,
                  border: `1px solid ${g.id === "event" ? `color-mix(in oklab, ${props.brandColor} 34%, ${LINE})` : LINE}`,
                  font: "inherit", color: INK,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600 }}>{e.label}</span>
                {e.gap ? (
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DANGER }}>Not wired</span>
                ) : e.source ? (
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: SUCCESS }}>Live data</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Canvas ───────────────────────────────────────────────────────────────── */

interface DragState {
  kind: "move" | "resize-x" | "resize-y" | "resize-xy";
  uid: string;
  startX: number;
  startY: number;
  origin: GridBox;
  colPx: number;
}

export function CustomLayoutCanvas(props: {
  blocks: CustomBlock[];
  device: DeviceId;
  selectedUid: string;
  brandColor: string;
  /** Width to draw the grid at. Comes from the host's `resolveDeviceWidths()`
   *  so it matches the device toggle in the toolbar above. */
  frameWidth?: number;
  onSelect: (uid: string) => void;
  onChange: (blocks: CustomBlock[], note: string) => void;
}): React.JSX.Element {
  const cols = DEVICE_COLS[props.device];
  const frameW = props.frameWidth && props.frameWidth > 0 ? props.frameWidth : DEVICE_FRAME[props.device];
  const rows = gridRows(props.blocks, props.device);
  const gridRef = React.useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [hover, setHover] = React.useState<{ c: number; r: number } | null>(null);

  const colPx = (): number => {
    const el = gridRef.current;
    if (!el) return 80;
    return (el.clientWidth - GRID_GAP * (cols - 1)) / cols;
  };

  const beginDrag = (ev: React.PointerEvent, kind: DragState["kind"], block: CustomBlock) => {
    ev.stopPropagation();
    ev.preventDefault();
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    props.onSelect(block.uid);
    setDrag({ kind, uid: block.uid, startX: ev.clientX, startY: ev.clientY, origin: resolveBox(block, props.device), colPx: colPx() });
  };

  const onPointerMove = (ev: React.PointerEvent) => {
    if (!drag) return;
    const dc = Math.round((ev.clientX - drag.startX) / (drag.colPx + GRID_GAP));
    const dr = Math.round((ev.clientY - drag.startY) / (ROW_PX + GRID_GAP));
    const o = drag.origin;
    /* Only the axes this gesture changes travel — see `placeWithPush`. */
    const patch: Partial<GridBox> = {};
    if (drag.kind === "move") {
      patch.c = clamp(o.c + dc, 0, cols - o.s);
      patch.r = Math.max(0, o.r + dr);
      if (patch.c === o.c && patch.r === o.r) return;
    }
    if (drag.kind === "resize-x" || drag.kind === "resize-xy") {
      patch.s = clamp(o.s + dc, 1, cols - o.c);
    }
    if (drag.kind === "resize-y" || drag.kind === "resize-xy") {
      patch.h = Math.max(1, o.h + dr);
    }
    if (drag.kind !== "move" && patch.s === o.s && patch.h === o.h) return;
    if (!Object.keys(patch).length) return;
    props.onChange(placeWithPush(props.blocks, props.device, drag.uid, patch), drag.kind === "move" ? "Moved a block" : "Resized a block");
  };

  const endDrag = () => setDrag(null);

  const onKeyDown = (ev: React.KeyboardEvent, block: CustomBlock) => {
    const keys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
    if (!keys.includes(ev.key)) return;
    ev.preventDefault();
    const o = resolveBox(block, props.device);
    const step = ev.key === "ArrowLeft" || ev.key === "ArrowUp" ? -1 : 1;
    const horizontal = ev.key === "ArrowLeft" || ev.key === "ArrowRight";
    /* One key per keystroke, so a nudge on mobile overrides one axis. */
    const patch: Partial<GridBox> = ev.shiftKey
      ? (horizontal ? { s: clamp(o.s + step, 1, cols - o.c) } : { h: Math.max(1, o.h + step) })
      : (horizontal ? { c: clamp(o.c + step, 0, cols - o.s) } : { r: Math.max(0, o.r + step) });
    props.onChange(placeWithPush(props.blocks, props.device, block.uid, patch), ev.shiftKey ? "Resized a block" : "Moved a block");
  };

  const cellFromEvent = (ev: React.DragEvent): { c: number; r: number } => {
    const el = gridRef.current;
    if (!el) return { c: 0, r: 0 };
    const rect = el.getBoundingClientRect();
    const c = clamp(Math.floor((ev.clientX - rect.left) / (colPx() + GRID_GAP)), 0, cols - 1);
    const r = Math.max(0, Math.floor((ev.clientY - rect.top) / (ROW_PX + GRID_GAP)));
    return { c, r };
  };

  const onDrop = (ev: React.DragEvent) => {
    const type = ev.dataTransfer.getData("application/x-jrni-block");
    setHover(null);
    if (!type) return;
    ev.preventDefault();
    const at = cellFromEvent(ev);
    const block = makeBlock(type, { c: props.device === "desktop" ? at.c : 0, r: at.r }, cols);
    /* A new block's position IS its desktop base (`makeBlock` set it), so the
       push runs with an empty patch — dropping one on mobile must not pin it. */
    const withBlock = props.blocks.concat([block]);
    props.onChange(placeWithPush(withBlock, props.device, block.uid, {}), `Added ${blockLabel(block)}`);
    props.onSelect(block.uid);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: SOFT }}>
          {DEVICE_LABEL[props.device]} · {cols} columns · {frameW}px
        </span>
      </div>

      <div
        style={{
          width: "100%", maxWidth: frameW, background: SURFACE,
          border: `1px solid ${LINE}`, borderRadius: 10, padding: 14,
        }}
      >
        <div
          ref={gridRef}
          onDragOver={(ev) => { ev.preventDefault(); setHover(cellFromEvent(ev)); }}
          onDragLeave={() => setHover(null)}
          onDrop={onDrop}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClick={() => props.onSelect("")}
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridAutoRows: `${ROW_PX}px`,
            gap: GRID_GAP,
            minHeight: rows * (ROW_PX + GRID_GAP),
            backgroundImage: `repeating-linear-gradient(90deg, color-mix(in oklab, ${props.brandColor} 5%, transparent) 0 calc(${100 / cols}% - ${GRID_GAP / 2}px), transparent calc(${100 / cols}% - ${GRID_GAP / 2}px) ${100 / cols}%)`,
          }}
        >
          {hover ? (
            <div
              aria-hidden="true"
              style={{
                gridColumn: `${hover.c + 1} / span 1`, gridRow: `${hover.r + 1} / span 1`,
                border: `2px dashed ${props.brandColor}`, borderRadius: 8,
                background: `color-mix(in oklab, ${props.brandColor} 8%, transparent)`,
                pointerEvents: "none",
              }}
            />
          ) : null}

          {props.blocks.map((block) => {
            const box = resolveBox(block, props.device);
            const selected = block.uid === props.selectedUid;
            const entry = PALETTE_BY_TYPE[block.type];
            const locked = Boolean(block.locked);
            const over = overriddenKeys(block, props.device);
            if (!box.v) {
              return (
                <div
                  key={block.uid}
                  onClick={(ev) => { ev.stopPropagation(); props.onSelect(block.uid); }}
                  style={{
                    gridColumn: `${box.c + 1} / span ${box.s}`, gridRow: `${box.r + 1} / span ${box.h}`,
                    border: `1px dashed ${LINE}`, borderRadius: 8, display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 8, cursor: "pointer", color: SOFT, fontSize: 12,
                  }}
                >
                  {blockLabel(block)} · hidden on {DEVICE_LABEL[props.device].toLowerCase()}
                </div>
              );
            }
            return (
              <div
                key={block.uid}
                tabIndex={0}
                role="group"
                aria-label={`${blockLabel(block)}, ${box.s} of ${cols} columns, ${box.h} rows`}
                onClick={(ev) => { ev.stopPropagation(); props.onSelect(block.uid); }}
                onKeyDown={(ev) => onKeyDown(ev, block)}
                onPointerDown={(ev) => { if (!locked || true) beginDrag(ev, "move", block); }}
                style={{
                  position: "relative",
                  gridColumn: `${box.c + 1} / span ${box.s}`,
                  gridRow: `${box.r + 1} / span ${box.h}`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  cursor: drag && drag.uid === block.uid ? "grabbing" : "grab",
                  overflow: "auto",
                  textAlign: box.a,
                  background: locked
                    ? `color-mix(in oklab, ${props.brandColor} 8%, ${SURFACE})`
                    : entry && entry.source
                      ? `color-mix(in oklab, ${props.brandColor} 4%, ${SURFACE})`
                      : SURFACE,
                  border: selected ? `2px solid ${props.brandColor}` : `1px solid ${LINE}`,
                  boxShadow: selected ? `0 0 0 3px color-mix(in oklab, ${props.brandColor} 18%, transparent)` : "none",
                  outline: "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: locked || (entry && entry.source) ? props.brandColor : SOFT }}>
                    {blockLabel(block)}
                  </span>
                  <span style={{ fontSize: 10, color: SOFT }}>{box.s}/{cols} · {box.h}r</span>
                  {locked ? <span style={{ fontSize: 10, color: SOFT }}>· locked</span> : null}
                  {over.length ? (
                    <span style={{ fontSize: 10, fontWeight: 700, color: WARN_TEXT }}>
                      · {DEVICE_LABEL[props.device].toLowerCase()} override
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: SOFT, marginTop: 6 }}>
                  {block.locked === "title"
                    ? "The event's own name and summary."
                    : block.locked === "cta"
                      ? "Register button. Cannot be hidden on any device."
                      : entry && entry.gap
                        ? entry.gap
                        : entry && entry.source
                          ? entry.source
                          : (typeof block.cfg.text === "string" && block.cfg.text) || "Authored here — nothing written yet."}
                </div>

                <span
                  aria-hidden="true"
                  onPointerDown={(ev) => beginDrag(ev, "resize-x", block)}
                  style={{ position: "absolute", right: -4, top: "50%", marginTop: -6, width: 12, height: 12, borderRadius: 3, background: SURFACE, border: `2px solid ${props.brandColor}`, cursor: "ew-resize", opacity: selected ? 1 : 0 }}
                />
                <span
                  aria-hidden="true"
                  onPointerDown={(ev) => beginDrag(ev, "resize-y", block)}
                  style={{ position: "absolute", left: "50%", marginLeft: -6, bottom: -4, width: 12, height: 12, borderRadius: 3, background: SURFACE, border: `2px solid ${props.brandColor}`, cursor: "ns-resize", opacity: selected ? 1 : 0 }}
                />
                <span
                  aria-hidden="true"
                  onPointerDown={(ev) => beginDrag(ev, "resize-xy", block)}
                  style={{ position: "absolute", right: -4, bottom: -4, width: 12, height: 12, borderRadius: 3, background: SURFACE, border: `2px solid ${props.brandColor}`, cursor: "nwse-resize", opacity: selected ? 1 : 0 }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Inspector ────────────────────────────────────────────────────────────── */

/** Config rows per block type — the fields the block genuinely reads. Kept
 *  deliberately short: anything the event record supplies is NOT editable copy
 *  here, it is a choice of whether to show it. */
const CFG_ROWS: Record<string, Array<[string, string, "line" | "area" | "tog"]>> = {
  text: [["text", "Text", "area"]],
  notice: [["text", "Text", "area"]],
  button: [["text", "Label", "line"], ["href", "Link", "line"]],
  quote: [["text", "Quote", "area"], ["who", "Who said it", "line"]],
  image: [["src", "Image URL", "line"], ["alt", "Alt text", "line"], ["caption", "Caption", "line"]],
  video: [["url", "Video URL", "line"], ["caption", "Caption", "line"]],
  gallery: [["urls", "Image URLs, one per line", "area"]],
  countdown: [["label", "Label", "line"]],
  eventdetails: [["showDate", "Show date and time", "tog"], ["showLocation", "Show location", "tog"], ["showAddToCalendar", "Show add to calendar", "tog"]],
  spaces: [["showBar", "Show the capacity bar", "tog"]],
  map: [["directions", "Show a directions link", "tog"]],
};

export function CustomLayoutInspector(props: {
  blocks: CustomBlock[];
  device: DeviceId;
  selectedUid: string;
  brandColor: string;
  onChange: (blocks: CustomBlock[], note: string) => void;
  onSelect: (uid: string) => void;
}): React.JSX.Element {
  const block = props.blocks.find((b) => b.uid === props.selectedUid) || null;
  const [warn, setWarn] = React.useState<{ axis: keyof GridBox; devices: DeviceId[]; apply: () => void } | null>(null);

  if (!block) {
    return (
      <div style={{ fontSize: 13, lineHeight: 1.6, color: SOFT }}>
        Nothing selected. Click a block on the page to change its width, height, order, alignment and content.
      </div>
    );
  }

  const cols = DEVICE_COLS[props.device];
  const box = resolveBox(block, props.device);
  const over = overriddenKeys(block, props.device);
  const entry = PALETTE_BY_TYPE[block.type];

  const writeBox = (patch: Partial<GridBox>, note: string) => {
    const axis = Object.keys(patch)[0] as keyof GridBox;
    /* The PATCH travels, not the resolved box — otherwise one width nudge on
       mobile would pin height, row and alignment too. */
    const apply = () => props.onChange(placeWithPush(props.blocks, props.device, block.uid, patch), note);
    // "Follow, but warn" (round 5): a desktop edit that a smaller device
    // overrides is shown before it is applied, never silently swallowed.
    const clash = props.device === "desktop" ? conflictsFor(props.blocks, block.uid, axis) : [];
    if (clash.length) { setWarn({ axis, devices: clash, apply }); return; }
    apply();
  };

  const clearOverride = (axis: keyof GridBox) => {
    const next = props.blocks.map((b) => {
      if (b.uid !== block.uid || props.device === "desktop") return b;
      const o = { ...(b[props.device] || {}) };
      delete o[axis];
      return { ...b, [props.device]: o } as CustomBlock;
    });
    props.onChange(next, "Cleared an override");
  };

  const setCfg = (key: string, value: unknown) => {
    props.onChange(
      props.blocks.map((b) => (b.uid === block.uid ? { ...b, cfg: { ...b.cfg, [key]: value } } : b)),
      "Edited block content",
    );
  };

  const remove = () => {
    props.onChange(props.blocks.filter((b) => b.uid !== block.uid), `Deleted ${blockLabel(block)}`);
    props.onSelect("");
  };

  const duplicate = () => {
    const copy: CustomBlock = {
      ...block,
      uid: newUid(block.type),
      locked: null,
      cfg: { ...block.cfg },
      desktop: { ...block.desktop, r: block.desktop.r + block.desktop.h },
      tablet: { ...block.tablet },
      mobile: { ...block.mobile },
    };
    props.onChange(placeWithPush(props.blocks.concat([copy]), props.device, copy.uid, {}), `Duplicated ${blockLabel(block)}`);
    props.onSelect(copy.uid);
  };

  const rowsField = (label: string, value: number, min: number, max: number, axis: keyof GridBox) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: SOFT }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button type="button" onClick={() => writeBox({ [axis]: Math.max(min, value - 1) } as Partial<GridBox>, `Changed ${label.toLowerCase()}`)} style={stepBtn}>−</button>
        <span style={{ minWidth: 54, textAlign: "center", fontSize: 13, fontWeight: 600 }}>
          {axis === "s" ? `${value} / ${cols}` : value}
        </span>
        <button type="button" onClick={() => writeBox({ [axis]: Math.min(max, value + 1) } as Partial<GridBox>, `Changed ${label.toLowerCase()}`)} style={stepBtn}>+</button>
        {over.includes(axis) ? (
          <button type="button" onClick={() => clearOverride(axis)} style={{ ...linkBtn, color: props.brandColor }}>Clear override</button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{blockLabel(block)}</span>
        <span style={{ fontSize: 12, lineHeight: 1.5, color: SOFT }}>
          {entry && entry.gap ? entry.gap : entry && entry.source ? entry.source : block.locked ? LOCKED_META[block.type].note : "Authored content."}
        </span>
      </div>

      {warn ? (
        <div style={{ border: `1px solid ${WARN_EDGE}`, background: WARN_BG, borderRadius: 8, padding: "10px 11px", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 12, lineHeight: 1.5, color: WARN_TEXT }}>
            {warn.devices.map((d) => DEVICE_LABEL[d]).join(" and ")} {warn.devices.length > 1 ? "have" : "has"} its own value for this. Applying the desktop change will overwrite it.
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => { warn.apply(); setWarn(null); }} style={{ ...stepBtn, padding: "6px 12px", fontWeight: 700 }}>Overwrite</button>
            <button type="button" onClick={() => setWarn(null)} style={{ ...stepBtn, padding: "6px 12px" }}>Keep it</button>
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: SOFT }}>
          Layout · {DEVICE_LABEL[props.device].toLowerCase()}
        </span>
        {rowsField("Width", box.s, 1, cols - box.c, "s")}
        {rowsField("Height in rows", box.h, 1, 12, "h")}
        {rowsField("Row", box.r, 0, 60, "r")}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: SOFT }}>Alignment</span>
          <div style={{ display: "inline-flex", border: `1px solid ${LINE}`, borderRadius: 7, overflow: "hidden", width: "fit-content" }}>
            {(["left", "center", "right"] as AlignId[]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => writeBox({ a }, "Changed alignment")}
                style={{
                  font: "inherit", fontSize: 12, padding: "6px 11px", border: 0, cursor: "pointer",
                  textTransform: "capitalize",
                  background: box.a === a ? props.brandColor : SURFACE,
                  color: box.a === a ? "var(--jrni-color-base-white)" : SOFT,
                }}
              >{a}</button>
            ))}
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: block.locked === "cta" ? "not-allowed" : "pointer" }}>
          <input
            type="checkbox"
            checked={box.v}
            disabled={block.locked === "cta"}
            onChange={(ev) => writeBox({ v: ev.target.checked }, "Changed visibility")}
          />
          <span style={{ fontSize: 13, color: block.locked === "cta" ? SOFT : INK }}>
            Visible on {DEVICE_LABEL[props.device].toLowerCase()}
          </span>
        </label>
        {block.locked === "cta" ? (
          <span style={{ fontSize: 12, lineHeight: 1.5, color: SOFT }}>
            The register button cannot be hidden. A page with no way to register should not be publishable.
          </span>
        ) : null}
      </div>

      {(CFG_ROWS[block.type] || []).length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: SOFT }}>Content</span>
          {(CFG_ROWS[block.type] || []).map(([key, label, kind]) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {kind === "tog" ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={block.cfg[key] !== false} onChange={(ev) => setCfg(key, ev.target.checked)} />
                  <span style={{ fontSize: 13, color: INK }}>{label}</span>
                </label>
              ) : (
                <>
                  <span style={{ fontSize: 12, color: SOFT }}>{label}</span>
                  {kind === "area" ? (
                    <textarea
                      rows={3}
                      value={String(block.cfg[key] ?? "")}
                      onChange={(ev) => setCfg(key, ev.target.value)}
                      style={{ font: "inherit", fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${LINE}`, resize: "vertical" }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={String(block.cfg[key] ?? "")}
                      onChange={(ev) => setCfg(key, ev.target.value)}
                      style={{ font: "inherit", fontSize: 13, padding: "7px 9px", borderRadius: 7, border: `1px solid ${LINE}` }}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" onClick={duplicate} style={{ ...stepBtn, flex: 1, padding: "8px 0" }}>Duplicate</button>
        <button
          type="button"
          onClick={remove}
          disabled={Boolean(block.locked)}
          title={block.locked ? "The title and register button cannot be deleted." : undefined}
          style={{ ...stepBtn, flex: 1, padding: "8px 0", color: block.locked ? SOFT : DANGER, opacity: block.locked ? 0.55 : 1, cursor: block.locked ? "not-allowed" : "pointer" }}
        >Delete</button>
      </div>
    </div>
  );
}

const stepBtn: React.CSSProperties = {
  font: "inherit", fontSize: 13, padding: "5px 10px", borderRadius: 7,
  border: `1px solid ${LINE}`, background: SURFACE, color: INK, cursor: "pointer",
};

const linkBtn: React.CSSProperties = {
  font: "inherit", fontSize: 12, fontWeight: 600, padding: 0,
  border: 0, background: "none", cursor: "pointer",
};
